import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Users, 
  Plus, 
  Search, 
  Filter, 
  MoreHorizontal,
  Edit,
  Trash2,
  Eye,
  UserCheck,
  UserX,
  Mail,
  BarChart3
} from 'lucide-react';
import { adminApi } from '../../../services/admin/adminApi';
import type { User, PaginatedResponse } from '../../../services/admin/types';
import CreateUserModal from '../../../components/admin/modals/CreateUserModal';
import EditUserModal from '../../../components/admin/modals/EditUserModal';

interface UsersPageState {
  users: User[];
  loading: boolean;
  error: string | null;
  pagination: PaginatedResponse<User>['pagination'] | null;
  filters: {
    search: string;
    user_type: string;
    is_active: string;
  };
  selectedUsers: string[];
  showCreateModal: boolean;
  showEditModal: boolean;
  editingUser: User | null;
}

export default function AdminUsers() {
  const [state, setState] = useState<UsersPageState>({
    users: [],
    loading: true,
    error: null,
    pagination: null,
    filters: {
      search: '',
      user_type: '',
      is_active: ''
    },
    selectedUsers: [],
    showCreateModal: false,
    showEditModal: false,
    editingUser: null
  });

  // Load users data
  const loadUsers = async (params?: any) => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const response = await adminApi.getUsers({
        search: state.filters.search || undefined,
        user_type: state.filters.user_type || undefined,
        is_active: state.filters.is_active ? state.filters.is_active === 'true' : undefined,
        limit: 10,
        offset: 0,
        ...params
      });
      
      setState(prev => ({
        ...prev,
        users: response.items,
        pagination: response.pagination,
        loading: false
      }));
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        error: error.response?.data?.detail || 'Failed to load users',
        loading: false
      }));
    }
  };

  // Load data on component mount
  useEffect(() => {
    loadUsers();
  }, []);

  // Handle search
  const handleSearch = (searchTerm: string) => {
    setState(prev => ({
      ...prev,
      filters: { ...prev.filters, search: searchTerm }
    }));
    // Debounce search
    const timeoutId = setTimeout(() => {
      loadUsers({ search: searchTerm });
    }, 500);
    return () => clearTimeout(timeoutId);
  };

  // Handle filter changes
  const handleFilterChange = (key: string, value: string) => {
    const newFilters = { ...state.filters, [key]: value };
    setState(prev => ({ ...prev, filters: newFilters }));
    loadUsers({
      search: newFilters.search || undefined,
      user_type: newFilters.user_type || undefined,
      is_active: newFilters.is_active ? newFilters.is_active === 'true' : undefined
    });
  };

  // Handle user selection
  const toggleUserSelection = (userId: string) => {
    setState(prev => ({
      ...prev,
      selectedUsers: prev.selectedUsers.includes(userId)
        ? prev.selectedUsers.filter(id => id !== userId)
        : [...prev.selectedUsers, userId]
    }));
  };

  // Helpers
  const formatPlanName = (name?: string) => {
    if (!name) return '—'
    const key = name.toLowerCase()
    const mapping: Record<string, string> = {
      free: 'Free',
      starter: 'Starter',
      professional: 'Professional',
      pro: 'Professional',
      business: 'Business',
      enterprise: 'Enterprise',
    }
    return mapping[key] || (key.charAt(0).toUpperCase() + key.slice(1))
  }

  const planBadgeClass = (name?: string) => {
    const key = (name || '').toLowerCase()
    switch (key) {
      case 'free':
      case 'starter':
        return 'bg-gray-100 text-gray-700'
      case 'professional':
      case 'pro':
        return 'bg-purple-100 text-purple-700'
      case 'business':
        return 'bg-blue-100 text-blue-700'
      case 'enterprise':
        return 'bg-amber-100 text-amber-800'
      default:
        return 'bg-gray-100 text-gray-700'
    }
  }

  const selectAllUsers = () => {
    setState(prev => ({
      ...prev,
      selectedUsers: prev.selectedUsers.length === prev.users.length 
        ? [] 
        : prev.users.map(user => user.id)
    }));
  };

  // Handle user actions
  const handleEditUser = (user: User) => {
    setState(prev => ({
      ...prev,
      editingUser: user,
      showEditModal: true
    }));
  };

  const handleDeleteUser = async (userId: string) => {
    if (!confirm('Are you sure you want to delete this user?')) return;
    
    try {
      await adminApi.deleteUser(userId);
      await loadUsers();
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        error: error.response?.data?.detail || 'Failed to delete user'
      }));
    }
  };

  const handleToggleUserStatus = async (user: User) => {
    const action = user.is_active ? 'disable' : 'enable';
    if (!confirm(`Are you sure you want to ${action} this user?`)) return;
    
    try {
      await adminApi.updateUser(user.id, { is_active: !user.is_active });
      await loadUsers();
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        error: error.response?.data?.detail || `Failed to ${action} user`
      }));
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getUserTypeColor = (userType: string) => {
    switch (userType) {
      case 'company_owner':
        return 'bg-blue-100 text-blue-800';
      case 'agent':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (isActive: boolean) => {
    return isActive 
      ? 'bg-green-100 text-green-800' 
      : 'bg-red-100 text-red-800';
  };

  if (state.loading && state.users.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Users</h1>
          <p className="text-gray-600">Manage platform users and their permissions</p>
        </div>
        <button
          onClick={() => setState(prev => ({ ...prev, showCreateModal: true }))}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add User
        </button>
      </div>

      {/* Error Alert */}
      {state.error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{state.error}</p>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search users by name, email..."
                value={state.filters.search}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* User Type Filter */}
          <select
            value={state.filters.user_type}
            onChange={(e) => handleFilterChange('user_type', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">All Types</option>
            <option value="company_owner">Company Owner</option>
            <option value="agent">Agent</option>
          </select>

          {/* Status Filter */}
          <select
            value={state.filters.is_active}
            onChange={(e) => handleFilterChange('is_active', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">All Status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={state.selectedUsers.length === state.users.length && state.users.length > 0}
                    onChange={selectAllUsers}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Plan
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Usage
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {state.users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <input
                      type="checkbox"
                      checked={state.selectedUsers.includes(user.id)}
                      onChange={() => toggleUserSelection(user.id)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                          <Users className="h-5 w-5 text-gray-600" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {user.full_name || 'No name'}
                        </div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getUserTypeColor(user.user_type)}`}>
                      {user.user_type.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(user.is_active)}`}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${planBadgeClass(user.subscription_plan)}`}>
                      {formatPlanName(user.subscription_plan)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {user.current_usage_messages}/{user.usage_limit_messages === -1 ? '∞' : user.usage_limit_messages}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {formatDate(user.created_at)}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Link 
                        to={`/admin/users/${user.id}/usage`}
                        className="text-gray-400 hover:text-purple-600 transition-colors"
                        title="View usage details"
                      >
                        <BarChart3 className="w-4 h-4" />
                      </Link>
                      <button
                        onClick={() => handleToggleUserStatus(user)}
                        className={`transition-colors ${
                          user.is_active 
                            ? 'text-gray-400 hover:text-orange-600' 
                            : 'text-gray-400 hover:text-green-600'
                        }`}
                        title={user.is_active ? 'Disable user' : 'Enable user'}
                      >
                        {user.is_active ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => handleEditUser(user)}
                        className="text-gray-400 hover:text-blue-600 transition-colors"
                        title="Edit user"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user.id)}
                        className="text-gray-400 hover:text-red-600 transition-colors"
                        title="Delete user"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {state.pagination && (
          <div className="bg-white px-6 py-3 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-500">
              Showing {state.pagination.offset + 1} to {Math.min(state.pagination.offset + state.pagination.limit, state.pagination.total)} of {state.pagination.total} results
            </div>
            <div className="flex gap-2">
              <button
                disabled={!state.pagination.has_prev}
                onClick={() => loadUsers({ offset: Math.max(0, state.pagination!.offset - state.pagination!.limit) })}
                className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>
              <button
                disabled={!state.pagination.has_next}
                onClick={() => loadUsers({ offset: state.pagination!.offset + state.pagination!.limit })}
                className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Empty State */}
      {!state.loading && state.users.length === 0 && (
        <div className="text-center py-12">
          <Users className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {state.filters.search || state.filters.user_type || state.filters.is_active
              ? 'Try adjusting your search or filter criteria.'
              : 'Get started by creating your first user.'}
          </p>
        </div>
      )}

      {/* Modals */}
      <CreateUserModal
        isOpen={state.showCreateModal}
        onClose={() => setState(prev => ({ ...prev, showCreateModal: false }))}
        onSuccess={() => loadUsers()}
      />
      
      <EditUserModal
        isOpen={state.showEditModal}
        onClose={() => setState(prev => ({ ...prev, showEditModal: false, editingUser: null }))}
        onSuccess={() => loadUsers()}
        user={state.editingUser}
      />
    </div>
  );
}
